import { useState, useReducer, useCallback, useMemo } from "react";
import questionsJson from "./data/questions.json";
import destinationsJson from "./data/destinations.json";
import type { AnswerEntry, Destination, Question, QuestionPool, DestinationCatalog } from "./lib/types";
import { buildProfile, rankDestinations } from "./lib/matcher";
import { deriveSequence, seededRng } from "./lib/sampler";
import { Landing } from "./components/Landing";
import { QuizQuestion } from "./components/QuizQuestion";
import { Loading } from "./components/Loading";
import { Result } from "./components/Result";

type Screen = "landing" | "intro" | "quiz" | "loading" | "result";

interface State {
  screen: Screen;
  questionIndex: number;
  questions: Question[];          // the picked question sequence for this session
  answers: AnswerEntry[];
  shownDestinations: string[];    // ordered; last item = current
  rerollCount: number;
  sessionId: string;
}

type Action =
  | { type: "START" }
  | { type: "ANSWER"; entry: AnswerEntry; nextQuestions: Question[] }
  | { type: "BACK" }
  | { type: "SUBMIT"; firstDestination: string }
  | { type: "REROLL"; nextDestination: string }
  | { type: "RESET"; questions: Question[]; sessionId: string };

function genSessionId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "START":
      return { ...state, screen: "quiz", questionIndex: 0 };
    case "ANSWER": {
      const answers = [...state.answers.filter((a) => a.questionId !== action.entry.questionId), action.entry];
      const next = state.questionIndex + 1;
      // The sampler may have grown the sequence (e.g., after S1 we now know
      // which branch dimensions apply). Use the freshly derived list.
      if (next >= action.nextQuestions.length) {
        return { ...state, answers, questions: action.nextQuestions, screen: "loading" };
      }
      return { ...state, answers, questions: action.nextQuestions, questionIndex: next };
    }
    case "BACK":
      return { ...state, questionIndex: Math.max(0, state.questionIndex - 1) };
    case "SUBMIT":
      return { ...state, screen: "result", shownDestinations: [action.firstDestination] };
    case "REROLL":
      return {
        ...state,
        rerollCount: state.rerollCount + 1,
        shownDestinations: [...state.shownDestinations, action.nextDestination],
      };
    case "RESET":
      return {
        screen: "landing",
        questionIndex: 0,
        questions: action.questions,
        answers: [],
        shownDestinations: [],
        rerollCount: 0,
        sessionId: action.sessionId,
      };
  }
}

const QUESTION_POOL = questionsJson as unknown as QuestionPool;
const CATALOG = destinationsJson as unknown as DestinationCatalog;

/** Initial question sequence for a session: one skill question (sampler grows it post-S1). */
function initialQuestions(sessionId: string): Question[] {
  return deriveSequence({
    pool: QUESTION_POOL.questions,
    answers: [],
    rng: seededRng(sessionId),
  });
}

// DEV PREVIEW: ?q=QUESTION_ID renders a single question directly.
const PREVIEW_QID = new URLSearchParams(window.location.search).get("q");
const PREVIEW_Q = PREVIEW_QID
  ? (QUESTION_POOL.questions.find(q => q.id === PREVIEW_QID) ?? null)
  : null;

export default function App() {
  const [initialState] = useState(() => {
    const sid = genSessionId();
    if (PREVIEW_Q) {
      return {
        screen: "quiz" as Screen,
        questionIndex: 0,
        questions: [PREVIEW_Q] as Question[],
        answers: [] as AnswerEntry[],
        shownDestinations: [] as string[],
        rerollCount: 0,
        sessionId: "preview",
      };
    }
    return {
      screen: "landing" as Screen,
      questionIndex: 0,
      questions: initialQuestions(sid),
      answers: [] as AnswerEntry[],
      shownDestinations: [] as string[],
      rerollCount: 0,
      sessionId: sid,
    };
  });
  const [state, dispatch] = useReducer(reducer, initialState);
  const [toast, setToast] = useState("");

  const handleQuizDone = useCallback(() => {
    const profile = buildProfile(state.answers);
    const ranked = rankDestinations(CATALOG.destinations, profile);
    const first = ranked[0]?.destination.id ?? CATALOG.destinations[0].id;
    void postSession(state.sessionId, state.answers, [first]);
    dispatch({ type: "SUBMIT", firstDestination: first });
  }, [state.answers, state.sessionId]);

  const handleReroll = useCallback(() => {
    const profile = buildProfile(state.answers);
    const exclude = new Set(state.shownDestinations);
    const ranked = rankDestinations(CATALOG.destinations, profile, exclude);
    if (ranked.length === 0) {
      setToast("没有更多匹配啦，要不要再测一次？");
      setTimeout(() => setToast(""), 2200);
      return;
    }
    if (state.rerollCount >= 3) {
      setToast("最后一个备选啦");
      setTimeout(() => setToast(""), 1700);
      return;
    }
    const next = ranked[0].destination.id;
    void postSession(state.sessionId, state.answers, [...state.shownDestinations, next]);
    dispatch({ type: "REROLL", nextDestination: next });
    setToast("懂了，那这个呢？");
    setTimeout(() => setToast(""), 1700);
  }, [state]);

  const restart = useCallback(() => {
    const sid = genSessionId();
    dispatch({ type: "RESET", questions: initialQuestions(sid), sessionId: sid });
  }, []);

  const onAnswerDispatch = useCallback(
    (entry: AnswerEntry) => {
      const nextAnswers = [
        ...state.answers.filter((a) => a.questionId !== entry.questionId),
        entry,
      ];
      const nextQuestions = deriveSequence({
        pool: QUESTION_POOL.questions,
        answers: nextAnswers,
        rng: seededRng(state.sessionId),
      });
      dispatch({ type: "ANSWER", entry, nextQuestions });
    },
    [state.answers, state.sessionId]
  );

  const currentDest: Destination | undefined = useMemo(() => {
    const id = state.shownDestinations[state.shownDestinations.length - 1];
    return CATALOG.destinations.find((d) => d.id === id);
  }, [state.shownDestinations]);

  const profile = useMemo(() => buildProfile(state.answers), [state.answers]);

  return (
    <div className="app-frame">
      {state.screen === "landing" && <Landing onStart={() => dispatch({ type: "START" })} />}
      {state.screen === "quiz" && (
        <QuizQuestion
          question={state.questions[state.questionIndex]}
          index={state.questionIndex}
          total={state.questions.length}
          existing={state.answers.find((a) => a.questionId === state.questions[state.questionIndex].id)}
          onAnswer={onAnswerDispatch}
          onBack={state.questionIndex > 0 ? () => dispatch({ type: "BACK" }) : undefined}
        />
      )}
      {state.screen === "loading" && <Loading onDone={handleQuizDone} />}
      {state.screen === "result" && currentDest && (
        <Result
          destination={currentDest}
          profile={profile}
          sessionId={state.sessionId}
          onReroll={handleReroll}
          onRestart={restart}
        />
      )}
      <div className={"toast" + (toast ? " show" : "")}>{toast}</div>
    </div>
  );
}

async function postSession(
  sessionId: string,
  answers: AnswerEntry[],
  resultDestinations: string[]
): Promise<void> {
  try {
    await fetch("/api/session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        session_id: sessionId,
        answers,
        result_destinations: resultDestinations,
        ref: new URLSearchParams(window.location.search).get("ref") ?? undefined,
      }),
    });
  } catch {
    // Fail silently — analytics shouldn't break UX.
  }
}
